import { useEffect, useRef, useState } from "react";
import { CreditCard, Lock } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";
import { PAYPAL_CLIENT_ID, PAYPAL_BUTTON_ID, PAYPAL_PACKAGE_BUTTONS } from "@/lib/constants";
import { CourseTerms } from "@/pages/TermsPage";
import { TERMS_VERSION } from "@/lib/terms";

const payLink = (bid) => `https://www.paypal.com/ncp/payment/${bid}`;

let sdk;
const loadSdk = () => {
  sdk = sdk || new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&components=buttons,hosted-buttons&enable-funding=venmo&currency=USD`;
    s.onload = () => resolve(window.paypal);
    s.onerror = () => { sdk = null; reject(new Error("PayPal could not load")); };
    document.body.appendChild(s);
  });
  return sdk;
};

function HostedButton({ slot, bid }) {
  const id = `paypal-container-${bid}-${slot}`;
  const done = useRef(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    loadSdk().then((pp) => pp.HostedButtons({ hostedButtonId: bid }).render(`#${id}`))
      .catch(() => setErr("PayPal couldn't load. Please refresh, or message Mr. Greg on WhatsApp."));
  }, [id, bid]);
  return (
    <div>
      <div id={id} data-testid={`paypal-button-${slot}`} />
      {err && <p className="text-xs text-rose-600" data-testid={`paypal-error-${slot}`}>{err}</p>}
    </div>
  );
}

function PackageButton({ slot, pay }) {
  const id = `paypal-pkg-${slot}`;
  const done = useRef(false);
  const [err, setErr] = useState("");
  const [paid, setPaid] = useState(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    loadSdk().then((pp) => pp.Buttons({
      style: { layout: "vertical", shape: "pill" },
      createOrder: (_, actions) => actions.order.create({ purchase_units: [{ description: pay.course.slice(0, 127), custom_id: pay.id, amount: { currency_code: "USD", value: pay.amount } }] }),
      onApprove: (_, actions) => actions.order.capture().then((o) => api.post(`/payments/${pay.id}/captured`, { order_id: o.id, paypal_status: o.status })).then(() => setPaid(true)),
      onError: () => setErr("PayPal couldn't finish the payment. Please try again or message Mr. Greg on WhatsApp."),
    }).render(`#${id}`)).catch(() => setErr("PayPal couldn't load. Please refresh, or message Mr. Greg on WhatsApp."));
  }, [id, pay]);
  if (paid) return <p className="rounded-xl bg-emerald-50 p-4 text-sm font-semibold text-emerald-800" data-testid={`paypal-paid-${slot}`}>Payment received — thank you! Mr. Greg will confirm your spot.</p>;
  return (
    <div>
      <div id={id} data-testid={`paypal-button-${slot}`} />
      {err && <p className="text-xs text-rose-600" data-testid={`paypal-error-${slot}`}>{err}</p>}
    </div>
  );
}

export function PayPalCheckout({ slot, prefill, addOns = [], addOnsOther = "", bare = false, pkg = "ai_course", title = "Pay the $5,000 course tuition" }) {
  const [f, setF] = useState({ name: prefill?.name || "", email: prefill?.email || "" });
  const [pay, setPay] = useState(null);
  const [busy, setBusy] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const start = async (e) => {
    e.preventDefault();
    if (!agreed) return toast.error("Please accept the course terms first.");
    setBusy(true);
    try {
      const { data } = await api.post("/payments/intent", { ...f, source: slot, add_ons: addOns, add_ons_other: addOnsOther, package: pkg, terms_accepted: true, terms_version: TERMS_VERSION });
      setPay(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  const ready = !!pay;
  const bid = pkg === "ai_course" ? PAYPAL_BUTTON_ID : PAYPAL_PACKAGE_BUTTONS[pkg];
  return (
    <div className={bare ? "flex flex-col gap-3" : "rounded-2xl border border-[#0B2A4A]/10 bg-[#FAF6EE] p-5 flex flex-col gap-3"} data-testid={`paypal-checkout-${slot}`}>
      {!bare && <p className="flex items-center gap-2 font-semibold text-[#0B2A4A]"><CreditCard className="h-4 w-4 text-[#F08A3C]" /> {title}</p>}
      {ready && !bid && <PackageButton slot={slot} pay={pay} />}
      {ready && bid ? (
        <>
          <HostedButton slot={slot} bid={bid} />
          <a href={payLink(bid)} target="_blank" rel="noopener noreferrer" className="text-center text-sm font-semibold text-[#0070BA] underline" data-testid={`paypal-link-${slot}`}>Button not loading? Pay by card or PayPal here</a>
        </>
      ) : !ready && (
        <form onSubmit={start} className="flex flex-col gap-2">
          <Input required placeholder="Your name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="bg-white" data-testid={`paypal-name-${slot}`} />
          <Input required type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="bg-white" data-testid={`paypal-email-${slot}`} />
          <CourseTerms checked={agreed} onChange={setAgreed} slot={slot} />
          <button disabled={busy || !agreed} className="rounded-xl bg-[#0070BA] text-white px-5 py-3 font-semibold hover:bg-[#005EA6] disabled:opacity-60" data-testid={`paypal-continue-${slot}`}>
            {busy ? "One moment..." : "Continue to secure checkout"}
          </button>
        </form>
      )}
      <p className="flex items-center gap-1.5 text-[11px] text-slate-500"><Lock className="h-3 w-3" /> Pay by credit or debit card (Visa, Mastercard, Amex, Discover), PayPal or Venmo — no PayPal account needed. Secure checkout powered by PayPal.{pkg === "ai_course" ? " Add-ons are quoted and billed separately." : ""}</p>
    </div>
  );
}
